import { useMutation, useQuery } from "@tanstack/react-query";

import { queryClient } from "../../../App";
import type { Reward, Subscription } from "../../handlers/rewards";
import { API } from "../apiService";
import { hasActiveUserAuthorization, useAuthorizationToken } from "../auth/authToken";

export const rewardsKey = ["payments", "rewards"] as const;

const STALE_TIME = 5 * 60 * 1000;

/**
 * The phone number is optional to the reader but not to the wire: the server validates it as a
 * string whenever the activity is a referral, so an omitted key fails where an empty one passes.
 */
export interface ReferralPayload {
	referredFullName: string;
	referredEmailAddress: string;
	referredPhoneNumber: string;
}

interface MutationCallbacks<TData> {
	onSuccess?: (data: TData) => void;
	onError?: (error: unknown) => void;
}

/**
 * Reward activities for the signed-in user.
 *
 * The GET is not a pure read: the server upserts a RESUME_COMPLETION reward for the account on every
 * call, so even a first-time reader comes back holding one. Nothing on the client has to create it.
 */
export function useRewards() {
	const token = useAuthorizationToken();

	return useQuery<Reward[]>({
		queryKey: rewardsKey,
		queryFn: async () => {
			const { data } = await API.get("payments/rewards");
			return Array.isArray(data) ? data : [];
		},
		retry: 0,
		staleTime: STALE_TIME,
		enabled: hasActiveUserAuthorization(token),
	});
}

/**
 * Refer someone, which also makes the server email them an invitation.
 *
 * A repeat referral of the same person comes back as a 409 carrying a readable reason, so callers
 * should surface the server's message rather than treat it as a generic failure.
 */
export function useCreateReferral({ onSuccess, onError }: MutationCallbacks<Reward> = {}) {
	return useMutation<Reward, unknown, ReferralPayload>({
		mutationFn: async (payload) => {
			const { data } = await API.post("payments/rewards", { ...payload, activityType: "REFERRAL" });
			return data;
		},
		onSuccess: (data) => {
			queryClient.invalidateQueries({ queryKey: rewardsKey });
			onSuccess?.(data);
		},
		onError: (error) => onError?.(error),
	});
}

/**
 * Collect the points on one reward.
 *
 * The same endpoint doubles as the referral nudge: below the server's completion threshold it emails
 * the referred person (at most once a week) and *still* answers 409, with the reason in its message.
 * That message is the useful part, so failures must not be flattened into a generic toast.
 */
export function useClaimReward({ onSuccess, onError }: MutationCallbacks<Reward> = {}) {
	return useMutation<Reward, unknown, string>({
		mutationFn: async (rewardId) => {
			const { data } = await API.patch(`payments/rewards/${rewardId}`);
			return data;
		},
		onSuccess: (data) => {
			queryClient.invalidateQueries({ queryKey: rewardsKey });
			onSuccess?.(data);
		},
		onError: (error) => onError?.(error),
	});
}

/**
 * Subscription and payment history for an account.
 *
 * `accountId` is the record's `_id`, which `useUserAccount()` already exposes as `userID` — there is
 * no second identity read to make.
 */
export function usePayments(accountId?: string) {
	return useQuery<Subscription[]>({
		queryKey: ["payments", accountId],
		queryFn: async () => {
			const { data } = await API.get("payments", { params: { accountId } });
			return Array.isArray(data) ? data : [];
		},
		retry: 0,
		staleTime: STALE_TIME,
		enabled: !!accountId,
	});
}
